import { useState, useEffect } from 'react';
import { Box, CircularProgress, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { getAuditLogs, type AuditLog } from '@lingaai/supabase-client';

export function AuditLogs() {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<AuditLog[]>([]);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        const data = await getAuditLogs(100);
        setLogs(data);
      } catch (error) {
        console.error('Error fetching audit logs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, []);

  const getActionColor = (action: string): 'success' | 'info' | 'error' | 'default' => {
    switch (action) {
      case 'INSERT':
        return 'success';
      case 'UPDATE':
        return 'info';
      case 'DELETE':
        return 'error';
      default:
        return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'created_at',
      headerName: 'Timestamp',
      flex: 1,
      minWidth: 180,
      valueFormatter: (value) => {
        return new Date(value).toLocaleString();
      },
    },
    {
      field: 'user',
      headerName: 'User',
      flex: 1,
      minWidth: 180,
      valueGetter: (_value, row) => {
        if (!row.user_id) return 'System';
        if (row.user?.full_name) return row.user.full_name;
        if (row.user?.email) return row.user.email;
        return 'Unknown';
      },
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 120,
      renderCell: (params: GridRenderCellParams) => <Chip label={params.value} color={getActionColor(params.value as string)} size="small" />,
    },
    {
      field: 'resource_type',
      headerName: 'Resource Type',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'resource_id',
      headerName: 'Resource ID',
      flex: 1,
      minWidth: 150,
      valueFormatter: (value: string) => {
        return value ? `${value.substring(0, 8)}...` : '-';
      },
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 120px)', width: '100%' }}>
      <DataGrid
        rows={logs}
        columns={columns}
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
        }}
      />
    </Box>
  );
}
